import {
  type Contenido,
  esCodigoValido,
  MIMES_IMAGEN_CONTENIDO,
  TAMANO_MAXIMO_IMAGEN_CONTENIDO,
} from "@gestiondoc/shared";
import type { JSONContent } from "@tiptap/core";
import {
  EditorContent,
  type Editor as EditorTipTap,
  useEditor,
  useEditorState,
} from "@tiptap/react";
import { type ReactNode, useRef, useState } from "react";
import { leerComoBase64 } from "../utiles.ts";
import { BLOQUES_NUEVOS, extensionesPara, referenciaNueva } from "./nodos.ts";
import { type ContextoDelEditor, ContextoEditor } from "./vistas.tsx";

/**
 * El editor por bloques de la pantalla 05: TipTap con los nodos de `nodos.ts`, una barra de
 * herramientas y el contexto que las vistas de bloque necesitan (plantilla, faltantes).
 */

export type SubirImagen = (archivo: {
  nombre: string;
  mime: (typeof MIMES_IMAGEN_CONTENIDO)[number];
  base64: string;
}) => Promise<{ archivoId: string }>;

export function useEditorDeContenido(opciones: {
  tipoCodigo: string;
  contenido: Contenido;
  editable: boolean;
  onCambio: (editor: EditorTipTap) => void;
}) {
  const { tipoCodigo, contenido, editable, onCambio } = opciones;
  const alCambiar = useRef(onCambio);
  alCambiar.current = onCambio;
  return useEditor(
    {
      extensions: extensionesPara(tipoCodigo),
      // El tipo de shared lleva `| undefined` explícito en los opcionales y el de TipTap no.
      content: contenido as JSONContent,
      editable,
      onUpdate: ({ editor }) => alCambiar.current(editor),
      editorProps: { attributes: { class: "ed-prosemirror", spellcheck: "true" } },
    },
    [tipoCodigo],
  );
}

export function Editor({
  editor,
  contexto,
  subirImagen,
  onAviso,
}: {
  editor: EditorTipTap;
  contexto: ContextoDelEditor;
  subirImagen: SubirImagen;
  onAviso: (mensaje: string | null) => void;
}) {
  return (
    <ContextoEditor.Provider value={contexto}>
      <div className="ed">
        {contexto.editable && (
          <BarraDeHerramientas editor={editor} subirImagen={subirImagen} onAviso={onAviso} />
        )}
        <EditorContent editor={editor} className="ed-contenido" />
      </div>
    </ContextoEditor.Provider>
  );
}

function BarraDeHerramientas({
  editor,
  subirImagen,
  onAviso,
}: {
  editor: EditorTipTap;
  subirImagen: SubirImagen;
  onAviso: (mensaje: string | null) => void;
}) {
  const entradaImagen = useRef<HTMLInputElement>(null);
  const [pidiendoReferencia, setPidiendoReferencia] = useState(false);
  const estado = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      underline: e.isActive("underline"),
      h2: e.isActive("heading", { level: 2 }),
      h3: e.isActive("heading", { level: 3 }),
      lista: e.isActive("bulletList"),
      enTabla: e.isActive("table"),
      enPaso: e.isActive("paso"),
      enNota: e.isActive("nota"),
      // Dentro de un paso, una celda o una nota no se meten bloques: sería anidar sin sentido.
      puedeBloque: !e.isActive("paso") && !e.isActive("table") && !e.isActive("nota"),
      horizontal: e.state.doc.attrs.orientacion === "horizontal",
      compacta: e.state.doc.attrs.densidad === "compacta",
      tablaDeFirmas: e.getAttributes("table").variante === "firmas",
      tablaSinBordes: e.getAttributes("table").variante === "sinBordes",
    }),
  });

  async function elegirImagen(archivo: File | undefined) {
    if (!archivo) return;
    const mime = archivo.type as (typeof MIMES_IMAGEN_CONTENIDO)[number];
    if (!MIMES_IMAGEN_CONTENIDO.includes(mime)) {
      onAviso("La imagen tiene que ser PNG o JPEG");
      return;
    }
    if (archivo.size > TAMANO_MAXIMO_IMAGEN_CONTENIDO) {
      onAviso("La imagen supera los 5 MB");
      return;
    }
    onAviso(null);
    try {
      const { archivoId } = await subirImagen({
        nombre: archivo.name,
        mime,
        base64: await leerComoBase64(archivo),
      });
      editor
        .chain()
        .focus()
        .insertContent({ type: "imagen", attrs: { archivoId, alt: "", ancho: 100 } })
        .run();
    } catch (e) {
      onAviso(e instanceof Error ? e.message : "No se pudo subir la imagen");
    }
  }

  function insertarReferencia(codigo: string) {
    const nodo = referenciaNueva(codigo);
    if (!nodo) return;
    editor
      .chain()
      .focus()
      .insertContent([nodo, { type: "text", text: " " }])
      .run();
    setPidiendoReferencia(false);
  }

  return (
    <div className="ed-barra" role="toolbar" aria-label="Formato">
      <Grupo>
        <BotonBarra
          activo={estado.bold}
          titulo="Negrita (Ctrl+B)"
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <b>B</b>
        </BotonBarra>
        <BotonBarra
          activo={estado.italic}
          titulo="Cursiva (Ctrl+I)"
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <i>I</i>
        </BotonBarra>
        <BotonBarra
          activo={estado.underline}
          titulo="Subrayado (Ctrl+U)"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <u>U</u>
        </BotonBarra>
      </Grupo>
      <Grupo>
        <BotonBarra
          activo={estado.h2}
          titulo="Subtítulo numerado (5.1)"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          Subtítulo
        </BotonBarra>
        <BotonBarra
          activo={estado.h3}
          titulo="Subtítulo de tercer nivel (5.1.1)"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          Sub-sub
        </BotonBarra>
        <BotonBarra
          activo={estado.lista}
          titulo="Lista con viñetas"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          • Lista
        </BotonBarra>
      </Grupo>
      <Grupo>
        <BotonBarra
          titulo="Lista de pasos con responsable"
          deshabilitado={!estado.puedeBloque}
          onClick={() => editor.chain().focus().insertContent(BLOQUES_NUEVOS.pasos).run()}
        >
          1. Pasos
        </BotonBarra>
        <BotonBarra
          titulo="Tabla de 3 × 3 con encabezado"
          deshabilitado={!estado.puedeBloque}
          onClick={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
        >
          + Tabla
        </BotonBarra>
        <BotonBarra
          titulo="Imagen (PNG o JPEG, hasta 5 MB)"
          deshabilitado={!estado.puedeBloque}
          onClick={() => entradaImagen.current?.click()}
        >
          + Imagen
        </BotonBarra>
        <input
          ref={entradaImagen}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={(e) => {
            void elegirImagen(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <BotonBarra
          titulo="Flujograma (Mermaid)"
          deshabilitado={!estado.puedeBloque}
          onClick={() => editor.chain().focus().insertContent(BLOQUES_NUEVOS.flujograma).run()}
        >
          + Flujograma
        </BotonBarra>
        <BotonBarra
          titulo="Lista de verificación (áreas y puntos que se marcan por hoja)"
          deshabilitado={!estado.puedeBloque}
          onClick={() =>
            editor.chain().focus().insertContent(BLOQUES_NUEVOS.listaDeVerificacion).run()
          }
        >
          + Verificación
        </BotonBarra>
        <BotonBarra
          titulo="Nota o advertencia"
          deshabilitado={!estado.puedeBloque}
          onClick={() => editor.chain().focus().insertContent(BLOQUES_NUEVOS.nota).run()}
        >
          + Nota
        </BotonBarra>
        <BotonBarra
          titulo="Referencia a otro documento, por código"
          activo={pidiendoReferencia}
          onClick={() => setPidiendoReferencia((v) => !v)}
        >
          + Referencia
        </BotonBarra>
      </Grupo>
      <Grupo>
        <BotonBarra
          activo={estado.horizontal}
          titulo="Hoja horizontal (carta apaisada), para formatos anchos"
          onClick={() =>
            editor
              .chain()
              .command(({ tr }) => {
                tr.setDocAttribute("orientacion", estado.horizontal ? null : "horizontal");
                return true;
              })
              .run()
          }
        >
          ▭ Horizontal
        </BotonBarra>
        <BotonBarra
          activo={estado.compacta}
          titulo="Letra compacta, para los formatos que en papel caben en una sola hoja"
          onClick={() =>
            editor
              .chain()
              .command(({ tr }) => {
                tr.setDocAttribute("densidad", estado.compacta ? null : "compacta");
                return true;
              })
              .run()
          }
        >
          Aa Compacta
        </BotonBarra>
      </Grupo>
      {estado.enTabla && (
        <Grupo>
          <BotonBarra
            titulo="Fila debajo"
            onClick={() => editor.chain().focus().addRowAfter().run()}
          >
            + Fila
          </BotonBarra>
          <BotonBarra
            titulo="Columna a la derecha"
            onClick={() => editor.chain().focus().addColumnAfter().run()}
          >
            + Columna
          </BotonBarra>
          <BotonBarra titulo="Quitar fila" onClick={() => editor.chain().focus().deleteRow().run()}>
            − Fila
          </BotonBarra>
          <BotonBarra
            titulo="Quitar columna"
            onClick={() => editor.chain().focus().deleteColumn().run()}
          >
            − Columna
          </BotonBarra>
          <BotonBarra
            titulo="Quitar la tabla"
            onClick={() => editor.chain().focus().deleteTable().run()}
          >
            Quitar tabla
          </BotonBarra>
          <BotonBarra
            activo={estado.tablaDeFirmas}
            titulo="Tabla de firmas: sin cajas, cada celda es una línea de firma con el cargo debajo"
            onClick={() =>
              editor
                .chain()
                .focus()
                .updateAttributes("table", { variante: estado.tablaDeFirmas ? null : "firmas" })
                .run()
            }
          >
            Firmas
          </BotonBarra>
          <BotonBarra
            activo={estado.tablaSinBordes}
            titulo="Tabla sin bordes: texto en columnas, sin rejilla"
            onClick={() =>
              editor
                .chain()
                .focus()
                .updateAttributes("table", {
                  variante: estado.tablaSinBordes ? null : "sinBordes",
                })
                .run()
            }
          >
            Sin bordes
          </BotonBarra>
        </Grupo>
      )}
      {pidiendoReferencia && (
        <FormularioReferencia
          onInsertar={insertarReferencia}
          onCancelar={() => setPidiendoReferencia(false)}
        />
      )}
    </div>
  );
}

function FormularioReferencia({
  onInsertar,
  onCancelar,
}: {
  onInsertar: (codigo: string) => void;
  onCancelar: () => void;
}) {
  const [codigo, setCodigo] = useState("");
  const valido = esCodigoValido(codigo);
  return (
    <form
      className="ed-referencia-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (valido) onInsertar(codigo);
      }}
    >
      <input
        // biome-ignore lint/a11y/noAutofocus: el formulario aparece al pedirlo; el foco va al campo
        autoFocus
        type="text"
        value={codigo}
        placeholder="GES-REG-019"
        onChange={(e) => setCodigo(e.target.value.toUpperCase())}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancelar();
        }}
        className="ed-entrada ed-entrada-codigo"
        aria-label="Código del documento"
      />
      <button type="submit" className="ed-boton-chico" disabled={!valido}>
        Insertar
      </button>
      <button type="button" className="ed-boton-chico ed-boton-suave" onClick={onCancelar}>
        Cancelar
      </button>
      <span className="ed-referencia-ayuda">
        Se comprueba que el documento exista al enviar a revisión.
      </span>
    </form>
  );
}

function Grupo({ children }: { children: ReactNode }) {
  return <div className="ed-barra-grupo">{children}</div>;
}

function BotonBarra({
  activo = false,
  deshabilitado = false,
  titulo,
  onClick,
  children,
}: {
  activo?: boolean;
  deshabilitado?: boolean;
  titulo: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={titulo}
      aria-pressed={activo}
      disabled={deshabilitado}
      // onMouseDown con preventDefault: el clic no le quita el foco al editor.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`ed-boton ${activo ? "ed-boton-activo" : ""}`}
    >
      {children}
    </button>
  );
}
